'use client';
import React from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { ArrowLeft, FileText } from 'lucide-react';

const sections = [
  {
    heading: 'Acceptance of Terms',
    content: 'By requesting access to or using the Panorama Community Network ("the Platform"), you agree to these Terms of Use. If you do not agree, do not use the Platform. These terms apply to all users, including verified residents, pending users, and visitors.',
  },
  {
    heading: 'Eligibility',
    content: 'The Platform is available to current residents of Panorama Towers (4525 and 4575 Dean Martin Drive, Las Vegas, NV). Eligibility requires:',
    list: [
      'Current residency at Panorama Towers as an owner, renter, or resident',
      'Completion of the verification process',
      'Agreement to these Terms of Use and the Community Guidelines',
      'You must be at least 18 years of age',
    ],
  },
  {
    heading: 'Platform Independence',
    content: 'The Platform is resident-operated and is not affiliated with, endorsed by, or operated by Panorama Towers building management, the HOA, or any property management entity. The Platform does not represent official building communications and should not be treated as such.',
  },
  {
    heading: 'User Conduct',
    content: 'You agree to use the Platform in a manner consistent with the Community Guidelines and applicable law. Prohibited conduct includes:',
    list: [
      'Posting false, misleading, or fraudulent content',
      'Harassing, threatening, or discriminating against other residents',
      'Sharing other residents\' private information without consent',
      'Using the Platform for commercial solicitation outside the designated services area',
      'Attempting to circumvent verification or access controls',
      'Posting content that violates applicable law',
    ],
  },
  {
    heading: 'Content Ownership',
    content: 'You retain ownership of content you post on the Platform. By posting content, you grant the Platform a non-exclusive license to display and distribute that content to other verified residents as part of normal Platform operation. You are responsible for ensuring you have the right to post any content you share.',
  },
  {
    heading: 'Verification & Access',
    content: 'Access to the full Platform requires verification of residency. The Platform reserves the right to deny, suspend, or revoke access at any time for violations of these Terms or the Community Guidelines. Verification does not guarantee permanent access.',
  },
  {
    heading: 'Listings & Services',
    content: 'Marketplace listings, service listings, and reviews are provided by residents and are not verified or endorsed by the Platform. The Platform is not a party to any transaction between residents. Use good judgment when engaging with listings and services.',
  },
  {
    heading: 'Moderation',
    content: 'The Platform team may remove content, suspend accounts, or take other moderation actions for violations of these Terms or the Community Guidelines. Moderation decisions are made by the Platform team, not building management. Appeals can be submitted through the Platform.',
  },
  {
    heading: 'Disclaimers',
    content: 'The Platform is provided "as is" without warranties of any kind. We do not guarantee the accuracy of resident-provided content, the availability of the Platform, or the outcome of any resident-to-resident interaction. The Platform is not responsible for disputes between residents.',
  },
  {
    heading: 'Limitation of Liability',
    content: 'To the maximum extent permitted by law, the Platform and its operators are not liable for any indirect, incidental, or consequential damages arising from your use of the Platform or interactions with other residents.',
  },
  {
    heading: 'Changes to Terms',
    content: 'We may update these Terms of Use from time to time. Significant changes will be communicated to verified residents. Continued use of the Platform after changes constitutes acceptance of the updated terms.',
  },
  {
    heading: 'Governing Law',
    content: 'These Terms are governed by the laws of the State of Nevada. Any disputes arising from these Terms shall be resolved in the courts of Clark County, Nevada.',
  },
];

export default function TermsOfUsePage() {
  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {/* Back nav */}
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-4">
            <Link href="/" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
              <ArrowLeft size={14} /> Back to Home
            </Link>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
          {/* Header */}
          <div className="flex items-start gap-4 mb-10">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(76,201,255,0.10)', border: '1px solid rgba(76,201,255,0.20)' }}
            >
              <FileText size={20} style={{ color: '#4CC9FF' }} />
            </div>
            <div>
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide mb-2"
                style={{ background: 'rgba(76,201,255,0.10)', border: '1px solid rgba(76,201,255,0.20)', color: '#4CC9FF' }}
              >
                Legal
              </span>
              <h1 className="font-display font-bold text-[32px] sm:text-[38px] leading-tight" style={{ color: '#F2F4FF' }}>
                Terms of Use
              </h1>
              <p className="text-[13px] mt-2" style={{ color: 'rgba(167,163,196,0.55)' }}>
                Last updated: March 2026 · Panorama Community Network
              </p>
            </div>
          </div>

          {/* Intro note */}
          <div
            className="px-5 py-4 rounded-xl mb-10"
            style={{ background: 'rgba(76,201,255,0.04)', border: '1px solid rgba(76,201,255,0.12)' }}
          >
            <p className="text-[13.5px] leading-relaxed" style={{ color: 'rgba(242,244,255,0.75)' }}>
              Please read these terms carefully. By using the Panorama Community Network, you agree to be bound by these Terms of Use and our Community Guidelines.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {sections?.map((section, idx) => (
              <div key={idx}>
                <h2 className="font-display font-semibold text-[18px] mb-3" style={{ color: '#F2F4FF' }}>
                  {section?.heading}
                </h2>
                {section?.content && (
                  <p className="text-[14.5px] leading-relaxed mb-3" style={{ color: '#A7A3C4' }}>
                    {section?.content}
                  </p>
                )}
                {section?.list && (
                  <ul className="space-y-2 mt-3">
                    {section?.list?.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: '#4CC9FF' }} />
                        <span className="text-[14px] leading-relaxed" style={{ color: '#A7A3C4' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {idx < sections?.length - 1 && (
                  <div className="neon-divider mt-10" />
                )}
              </div>
            ))}
          </div>

          {/* Footer links */}
          <div
            className="mt-14 pt-8 flex flex-wrap gap-4"
            style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Link href="/privacy-policy" className="text-[13px] font-medium transition-colors duration-150 hover:opacity-80" style={{ color: '#4CC9FF' }}>
              Privacy Policy →
            </Link>
            <Link href="/community-guidelines" className="text-[13px] font-medium transition-colors duration-150 hover:opacity-80" style={{ color: '#4CC9FF' }}>
              Community Guidelines →
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
